import { CanvasItemLayoutDescDictionary, CanvasLayout } from '@UI/Canvas/Canvas';
import { CanvasItemLayoutDesc } from '@UI/Canvas/Structs';
import { isDefined } from '@Utils/Micro';
import { DebugEx } from '@Utils/DebugEx';
import { JsonSchemaValidator } from '@Utils/JsonSchemaValidator';
import { DomUtils } from "@Utils/Dom"
import _ from 'lodash';
import { GatewaySettings, AssetTrackerWidgetData } from './AssetTackerStructs';
// =======================================================================================================
export default class AssetTrackerUtils {
    /**
     * Get `Yodiwo.UI.Canvas.CanvasLayout`
     * @param layout (Note: object passed in is mutated)
     * @param visibleIds
     */
    public static getCanvasLayout(layout: CanvasItemLayoutDescDictionary, gateways: { [key: string]: string }, visibleIds: string[] = null): CanvasLayout {
        if (isDefined(visibleIds))
            layout = _.omit(_.keyBy(_.map(visibleIds, (id) => layout[id]), 'Key'), undefined);

        return {
            LayoutDesc: layout,
            ItemDesc: _.mapValues(layout, (value) => {
                return {
                    Key: value.Key,
                    Render: (key: string) => <div
                        style={
                            {
                                wordWrap: 'break-word',
                                position: 'absolute',
                                bottom: 0
                            }
                        }
                        key={key}
                    >
                        {gateways[key]}
                    </div>
                }
            })
        }
    }
    // ------------------------------------------------------------------------------------------------------
    public static getDefaultCanvasItemLayoutDesc(key: string): CanvasItemLayoutDesc {
        return {
            Key: key,

            X: 0,
            Y: 0,

            H: 3,
            W: 3,

            IsDraggable: true,
            IsResizable: false,
            IsStatic: false,

            MaxH: 3,
            MaxW: 3,

            MinH: 3,
            MinW: 3,

            DefiesGrid: true
        }
    }
    // ------------------------------------------------------------------------------------------------
    public static getDefaultCanvasLayoutDesc(key: string): CanvasItemLayoutDescDictionary {
        return { [key]: AssetTrackerUtils.getDefaultCanvasItemLayoutDesc(key) };
    }
    // ------------------------------------------------------------------------------------------------
    public static getDefaultGatewaySettings(): GatewaySettings {
        return {
            circles: {
                quarters: [true, true, true, true],
                radiusCoefficient: 1,
                color: '#000000',
                thickness: 2,
                polarAngle: 0
            }
        }
    }
    // ------------------------------------------------------------------------------------------------
    public static getDefaultGatewaySettingsDict(key: string): { [key: string]: GatewaySettings } {
        return { [key]: AssetTrackerUtils.getDefaultGatewaySettings() };
    }
    // ------------------------------------------------------------------------------------------------------
    /**
     * Migrate widget's data.
     *
     * @param {AssetTrackerWidgetData} widgetData
     * @returns {AssetTrackerWidgetData}
     */
    public static migrateWidgetData(widgetData: AssetTrackerWidgetData): AssetTrackerWidgetData {

        // Check if migration is needed for background image.
        if (isDefined(widgetData['BackgroundImgSrc'])) {
            DomUtils.getResizedImage(widgetData['BackgroundImgSrc'], 1920, 1080, 'jpeg')
                .then((imgInfo) => {
                    widgetData.BackgroundImgInfo = imgInfo;
                    // Delete property.
                    delete widgetData['BackgroundImgSrc'];
                })
                .catch((er) => DebugEx.TraceCriticalException(er));
        }

        // Check if migration is needed for quarters.
        if (isDefined(widgetData['GatewayQuarters'])) {
            try {
                // Set quarters for assets.
                _.forOwn(widgetData['GatewayQuarters'], (value, key) => {
                    if (!isDefined(widgetData.GatewaySettings[key])) {
                        widgetData.GatewaySettings[key] = AssetTrackerUtils.getDefaultGatewaySettings();
                    }

                    widgetData.GatewaySettings[key].circles.quarters = value
                });

                // Delete property.
                delete widgetData['GatewayQuarters'];
            }
            catch (e) {
                DebugEx.TraceError(e);
            }
        }

        // Check if migration is needed for radius coefficient.
        if (isDefined(widgetData['RadiusCoefficients'])) {
            try {
                // Set quarters for assets.
                _.forOwn(widgetData['RadiusCoefficients'], (value, key) => {
                    if (!isDefined(widgetData.GatewaySettings[key])) {
                        widgetData.GatewaySettings[key] = AssetTrackerUtils.getDefaultGatewaySettings();
                    }
                    widgetData.GatewaySettings[key].circles.radiusCoefficient = value
                });

                // Delete property.
                delete widgetData['RadiusCoefficients'];
            }
            catch (e) {
                DebugEx.TraceError(e);
            }
        }

        // Check if migration is needed for the color of circles.
        if (isDefined(widgetData['CircleColors'])) {
            try {
                // Set quarters for assets.
                _.forOwn(widgetData['CircleColors'], (value, key) => {
                    if (!isDefined(widgetData.GatewaySettings[key])) {
                        widgetData.GatewaySettings[key] = AssetTrackerUtils.getDefaultGatewaySettings();
                    }
                    widgetData.GatewaySettings[key].circles.color = value;
                });

                // Delete property.
                delete widgetData['CircleColors'];
            }
            catch (e) {
                DebugEx.TraceError(e);
            }
        }

        return widgetData;
    }
    // ------------------------------------------------------------------------------------------------------
    public static gatewayInfoSchema = {
        type: "object",
        properties: {
            Id: { type: "string" },
            FriendlyName: { type: "string" },
            Description: { type: "string" },
            LocationId: { type: ["number", "string"] },
            Data: { type: "string" }
        },
        required: ["Id"]
    };
    public static gatewayInfoValidator() {
        return JsonSchemaValidator.GetInstance('AssetTracker_gatewayInfoValidator', {
            title: "GatewayInfo",
            oneOf: [
                {
                    type: "array",
                    items: AssetTrackerUtils.gatewayInfoSchema
                },
                {
                    type: "object",
                    patternProperties: {
                        "^.*$": AssetTrackerUtils.gatewayInfoSchema
                    }
                }
            ]
        });
    };
    // ------------------------------------------------------------------------------------------------------
    public static assetsInfoValidator() {
        return JsonSchemaValidator.GetInstance('AssetTracker_assetsInfoValidator', {
            title: "AssetInfo",
            type: "object",
            minProperties: 1,
            patternProperties: {
                "^.*$": {
                    type: "array",
                    items: {
                        type: "object",
                        properties: {
                            Id: { type: "string" },
                            AssetName: { type: "string" },
                            IsLost: { type: "boolean" },
                            IsRemoved: { type: "boolean" },
                            ProximityLevel: { "enum": ["Near", "Immediate", "Far"] },
                            Data: { type: "string" },
                            Radius: { type: "number" },
                            PolarAngle: { type: "number" }
                        },
                        required: ["Id"]
                    }
                }
            }
        });
    }
    // ------------------------------------------------------------------------------------------------------
    public static settingsValidator() {
        return JsonSchemaValidator.GetInstance('AssetTracker_settingsValidator', {
            title: 'AssetTracker',
            type: 'object',
            properties: {
                BackgroundImgInfo: {
                    type: 'object',
                    properties: {
                        src: { type: 'string' },
                        width: { type: 'number' },
                        height: { type: 'number' },
                        aspectRatio: { type: 'number' }
                    },
                    required: ['src', 'width', 'height', 'aspectRatio']
                },
                Layout: {
                    type: 'object',
                    patternProperties: {
                        '^.*$': {
                            type: 'object',
                            properties: {
                                Key: { type: 'string' },
                                X: { type: 'number' },
                                Y: { type: 'number' },
                                W: { type: 'number' },
                                H: { type: 'number' },
                                IsDraggable: { type: 'boolean' },
                                IsResizable: { type: 'boolean' },
                                IsStatic: { type: 'boolean' },
                                MaxH: { type: 'number' },
                                MaxW: { type: 'number' },
                                MinH: { type: 'number' },
                                MinW: { type: 'number' },
                                DefiesGrid: { type: 'boolean' },
                                WidgetNoBackground: { type: 'boolean' },
                                MobileOrder: { type: 'number' },
                            },
                            required: ['Key', 'X', 'Y', 'W', 'H']
                        }
                    }
                },
                VisibleGateways: {
                    type: 'array',
                    items: { type: ['string'] }
                },
                GatewaySettings: {
                    type: 'object',
                    patternProperties: {
                        '^.*$': {
                            type: 'object',
                            properties: {
                                circles: {
                                    type: 'object',
                                    properties: {
                                        quarters: {
                                            type: 'array',
                                            items: {
                                                type: ['boolean'],
                                                maxItems: 4,
                                                minItems: 4
                                            }
                                        },
                                        radiusCoefficient: { type: 'number' },
                                        color: { type: 'string' },
                                        thickness: { type: 'number' },
                                        polarAngle: { type: 'number' }
                                    },
                                    required: [
                                        'quarters',
                                        'radiusCoefficient',
                                        'color',
                                        'thickness',
                                        'polarAngle'
                                    ]
                                }
                            },
                            required: ['circles']
                        }
                    }
                },
                TooltipSettings: {
                    type: 'object',
                    properties: {
                        fontSize: { type: 'number' },
                        color: { type: 'string' }
                    },
                    required: ['fontSize', 'color']
                }
            }
        });
    }
}
// =======================================================================================================